import { useEffect, useMemo, useState } from "react";
import { appstr } from "@coreapp/utils/appstr";
import { enumBoxStyleSet, enumHierarchyTypes } from "@coreapp/constants";
import type { BoxStyleEntry } from "./editedOrderUtils";

export const SORT_TYPE = {
  A_Z: "a-z",
  Z_A: "z-a",
  MOST_USED: "most-used",
  LEAST_USED: "least-used",
  SYSTEM_FIRST: "system-first",
  SHARED_FIRST: "shared-first",
  EMPLOYEE_FIRST: "employee-first",
  POSITION_FIRST: "position-first",
  GROUP_FIRST: "group-first"
} as const;

export type SortKey = (typeof SORT_TYPE)[keyof typeof SORT_TYPE];
const SORT_KEYS = Object.values(SORT_TYPE) as SortKey[];
const BOX_STYLE_SORT_SESSION_KEY = "box-styles-sort";

const isSortKey = (value: string): value is SortKey => {
  return SORT_KEYS.includes(value as SortKey);
};

const getStoredSortKey = (): SortKey => {
  try {
    const storedSort = sessionStorage.getItem(BOX_STYLE_SORT_SESSION_KEY);
    if (storedSort && isSortKey(storedSort)) {
      return storedSort;
    }
  } catch {
    // Ignore storage access errors and use default sort.
  }

  return SORT_TYPE.MOST_USED;
};

export const useSortBoxStyles = (
  boxStyles: BoxStyleEntry[],
  boxStyleCounts: Map<string, number>,
  isPositionChart: boolean,
  canAccessGrouping: boolean
) => {
  const [sortBy, setSortBy] = useState<SortKey>(() => getStoredSortKey());

  useEffect(() => {
    try {
      sessionStorage.setItem(BOX_STYLE_SORT_SESSION_KEY, sortBy);
    } catch {
      // Ignore storage access errors and keep state in-memory.
    }
  }, [sortBy]);

  const sortedBoxStyles = useMemo(
    () =>
      [...boxStyles].sort((a, b) => {
        switch (sortBy) {
          case SORT_TYPE.A_Z:
            return a.layout.GetBoxStyleName().localeCompare(b.layout.GetBoxStyleName());
          case SORT_TYPE.Z_A:
            return b.layout.GetBoxStyleName().localeCompare(a.layout.GetBoxStyleName());
          case SORT_TYPE.MOST_USED:
            return (
              (boxStyleCounts.get(b.layout.GetBoxStyleID()) || 0) -
              (boxStyleCounts.get(a.layout.GetBoxStyleID()) || 0)
            );
          case SORT_TYPE.LEAST_USED:
            return (
              (boxStyleCounts.get(a.layout.GetBoxStyleID()) || 0) -
              (boxStyleCounts.get(b.layout.GetBoxStyleID()) || 0)
            );
          case SORT_TYPE.SYSTEM_FIRST:
            return (
              (a.boxStyleSet === enumBoxStyleSet.systemboxstyles ? 0 : 1) -
              (b.boxStyleSet === enumBoxStyleSet.systemboxstyles ? 0 : 1)
            );
          case SORT_TYPE.SHARED_FIRST:
            return (
              (a.boxStyleSet === enumBoxStyleSet.sharedboxstyles ? 0 : 1) -
              (b.boxStyleSet === enumBoxStyleSet.sharedboxstyles ? 0 : 1)
            );
          case SORT_TYPE.EMPLOYEE_FIRST:
            return (
              ((a.layout.GetHierarchyType() ?? 0) === enumHierarchyTypes.employees ? 0 : 1) -
              ((b.layout.GetHierarchyType() ?? 0) === enumHierarchyTypes.employees ? 0 : 1)
            );
          case SORT_TYPE.POSITION_FIRST:
            return (
              ((a.layout.GetHierarchyType() ?? 0) === enumHierarchyTypes.positions ? 0 : 1) -
              ((b.layout.GetHierarchyType() ?? 0) === enumHierarchyTypes.positions ? 0 : 1)
            );
          case SORT_TYPE.GROUP_FIRST:
            return (
              ((a.layout.GetHierarchyType() ?? 0) === enumHierarchyTypes.grouping ? 0 : 1) -
              ((b.layout.GetHierarchyType() ?? 0) === enumHierarchyTypes.grouping ? 0 : 1)
            );
          default:
            return 0;
        }
      }),
    [boxStyles, boxStyleCounts, sortBy]
  );

  const sortOptions = [
    { value: SORT_TYPE.A_Z, label: appstr("A to Z"), clickHandler: () => setSortBy(SORT_TYPE.A_Z) },
    {
      value: SORT_TYPE.Z_A,
      label: appstr("Z to A"),
      divider: true,
      clickHandler: () => setSortBy(SORT_TYPE.Z_A)
    },
    {
      value: SORT_TYPE.MOST_USED,
      label: appstr("Most used"),
      clickHandler: () => setSortBy(SORT_TYPE.MOST_USED)
    },
    {
      value: SORT_TYPE.LEAST_USED,
      label: appstr("Least used"),
      divider: true,
      clickHandler: () => setSortBy(SORT_TYPE.LEAST_USED)
    },
    {
      value: SORT_TYPE.SYSTEM_FIRST,
      label: appstr("System box styles first"),
      clickHandler: () => setSortBy(SORT_TYPE.SYSTEM_FIRST)
    },
    {
      value: SORT_TYPE.SHARED_FIRST,
      label: appstr("Shared box styles first"),
      divider: canAccessGrouping || isPositionChart,
      clickHandler: () => setSortBy(SORT_TYPE.SHARED_FIRST)
    },
    ...(isPositionChart
      ? [
          {
            value: SORT_TYPE.EMPLOYEE_FIRST,
            label: appstr("Employee box styles first"),
            clickHandler: () => setSortBy(SORT_TYPE.EMPLOYEE_FIRST)
          },
          {
            value: SORT_TYPE.POSITION_FIRST,
            label: appstr("Position box styles first"),
            clickHandler: () => setSortBy(SORT_TYPE.POSITION_FIRST)
          }
        ]
      : canAccessGrouping
        ? [
            {
              value: SORT_TYPE.EMPLOYEE_FIRST,
              label: appstr("Employee box styles first"),
              clickHandler: () => setSortBy(SORT_TYPE.EMPLOYEE_FIRST)
            },
            {
              value: SORT_TYPE.GROUP_FIRST,
              label: appstr("Group box styles first"),
              clickHandler: () => setSortBy(SORT_TYPE.GROUP_FIRST)
            }
          ]
        : [])
  ];

  return { sortedBoxStyles, sortOptions, sortBy };
};
